import moment from 'moment';
import { useQuery } from 'react-query';
import { addDays, subDays } from 'date-fns';
import { useCurrentClientAtom } from 'atoms/clientAtom';
import { downloadImage, downloadXLS } from 'utils/download';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCurrentClientCurrency } from 'hooks/useCurrentClientCurrency';

// COMPONENTS
import MultiDropdown from '../MultiDropdown';
import LoadingSpinner from '../LoadingSpinner';
import SectionMessage from '../SectionMessage';
import TooltipPopover from '../TooltipPopover';
import Card, { CardProps } from '../AO_comp/Card';
import FilterBarWrapper from '../FilterBarWrapper';
import OrganicPaidImp from '../AO_comp/OrganicPaidImp';
import DatepickerComponent from '../DatepickerComponent';
import CostVsExpectCost from '../AO_comp/CostVsExpectCost';
import OrganicPaidClick from '../AO_comp/OrganicPaidClick';
import ChangeLog, { ChangeLogData } from '../AO_comp/ChangeLog';

// TYPES
import { DateValueType } from 'react-tailwindcss-datepicker';

// UTILS
import { formatCurrencySymbol } from 'utils';

const initialChangeLogData: ChangeLogData = {
  change_log: {},
  change_log_daily: {},
  change_log_summary: {
    on: 0,
    off: 0,
  },
  change_log_table: [],
};

function getCSRFToken() {
  const cookieValue = document.cookie
    .split('; ')
    .find((row) => row.startsWith('csrftoken='))
    ?.split('=')[1];
  return cookieValue;
}

const fetchOsabData = async (
  dateRange: DateValueType,
  keywords: string = '',
  market: string = 'US'
) => {
  if (!dateRange?.endDate || !dateRange.startDate) {
    throw new Error('Start and end dates required.');
  }

  const csrfToken = getCSRFToken();
  const response = await fetch('/get_osab_data/', {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json',
      ...(csrfToken && { 'X-CSRFToken': csrfToken }),
    }),
    body: JSON.stringify({
      date_from: moment(dateRange.startDate).format('YYYY-MM-DD'),
      date_to: moment(dateRange.endDate).format('YYYY-MM-DD'),
      keyword: keywords,
      market,
    }),
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
};

function AutoOptimizeOverview() {
  const [cardData, setCardData] = useState<CardProps[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedKeywords, setSelectedKeywords] = useState<string>('');
  const [{ current_client, current_client_market }] = useCurrentClientAtom();
  const [searchParams] = useSearchParams();
  const usecases = searchParams.get('usecases');
  const { currencySymbol } = useCurrentClientCurrency();

  // Add space if the toLocalString() returns currency code instead of symbol, when symbol for that currency is not available (for readability)
  const currencySymbolWithSpace = formatCurrencySymbol(currencySymbol);

  const [dailyCostVsExpectedData, setDailyCostVsExpectedData] = useState<
    Record<string, { cost: number; expected_cost: number }>
  >({});
  const [weeklyCostVsExpectedData, setWeeklyCostVsExpectedData] = useState<
    Record<string, { cost: number; expected_cost: number }>
  >({});
  const [organicVsPaidClicksData, setOrganicVsPaidClicksData] = useState<
    Record<string, { organic_clicks: number; paid_clicks: number }>
  >({});
  const [organicVsPaidImpressionsData, setOrganicVsPaidImpressionsData] =
    useState<
      Record<string, { organic_impressions: number; paid_impressions: number }>
    >({});
  const [changeLogData, setChangeLogData] =
    useState<ChangeLogData>(initialChangeLogData);

  const [dateRange, setValue] = useState<DateValueType>({
    startDate: addDays(new Date(), -90), // 90 days before today
    endDate: subDays(new Date(), 4), // Today minus 4 days
  });

  useEffect(() => {
    if (usecases === 'saving_trends') {
      setValue({
        startDate: subDays(new Date(), 10), // 14 days ago
        endDate: subDays(new Date(), 4), // Yesterday
      });
    }
  }, [usecases]);

  const keywordOptions = useMemo(() => {
    if (!changeLogData?.change_log) return [];

    const dates = Object.keys(changeLogData.change_log).sort();
    const recentDate = dates[dates.length - 1];

    return (
      changeLogData.change_log?.[recentDate]?.map(({ keyword }) => keyword) ||
      []
    );
  }, [changeLogData.change_log]);

  const { data, error, isLoading, refetch } = useQuery(
    [
      'osabData',
      dateRange?.startDate,
      dateRange?.endDate,
      current_client_market,
      current_client,
      selectedKeywords,
    ],
    () => fetchOsabData(dateRange, selectedKeywords, current_client_market),
    {
      enabled:
        !!dateRange?.startDate &&
        !!dateRange?.endDate &&
        !!current_client_market,
      staleTime: 1000 * 60 * 60,
    }
  );

  useEffect(() => {
    if (data) {
      const formatNumber = (value: number) => value.toLocaleString();

      const getPercentageChange = (value: number | null) =>
        value !== null && typeof value === 'number'
          ? `${value.toFixed(1)}%`
          : 'N/A';
      const getIsPositiveOrNA = (value: number | null) => {
        if (typeof value === 'string' && value === 'Invalid') {
          return 'Invalid';
        }
        return value === null || value > 0;
      };

      const summaryData = data.summary;
      const diffData = data.diff_summary;

      const cardProps: CardProps[] = [
        {
          title: 'Estimated Savings',
          value: `${currencySymbolWithSpace}${formatNumber(parseInt(summaryData.estimated_savings))}`,
          change: `${getPercentageChange(diffData.estimated_savings)}`,
          isPositive: getIsPositiveOrNA(diffData.estimated_savings),
          styleType: 'light',
          tooltipText: 'Estimated savings generated',
          tooltipContent: 'auto_optimize_page.performance.estimated_savings',
          tooltipPosition: 'above',
        },
        {
          title: 'Holistic CPC',
          value: `${currencySymbolWithSpace}${formatNumber(summaryData.hollistic_cpc.toFixed(2))}`,
          change: `${getPercentageChange(diffData.hollistic_cpc)}`,
          isPositive: getIsPositiveOrNA(diffData.hollistic_cpc),
          styleType: 'light',
          tooltipText: [
            'Paid Search cost divided by',
            'Paid Search and Organic Search clicks',
          ],
          tooltipContent: 'auto_optimize_page.performance.holistic_cpc',
          invert: true,
        },
        {
          title: 'Total Clicks',
          value: `${formatNumber(summaryData.total_clicks)}`,
          change: `${getPercentageChange(diffData.total_clicks)}`,
          isPositive: getIsPositiveOrNA(diffData.total_clicks),
          styleType: 'light',
          tooltipText: 'Organic and Paid Search clicks',
          tooltipContent: 'auto_optimize_page.performance.total_clicks',
          tooltipPosition: 'above',
        },
        {
          title: 'Organic CTR(%)',
          value: `${formatNumber(summaryData.organic_ctr)}%`,
          change: `${getPercentageChange(diffData.organic_ctr)}`,
          isPositive: getIsPositiveOrNA(diffData.organic_ctr),
          styleType: 'light',
          tooltipContent:
            'auto_optimize_page.performance.organic_ctr_percentage',
        },
        {
          title: 'Organic Impressions',
          value: `${formatNumber(summaryData.organic_impressions)}`,
          change: `${getPercentageChange(diffData.organic_impressions)}`,
          isPositive: getIsPositiveOrNA(diffData.organic_impressions),
          tooltipContent: 'auto_optimize_page.performance.organic_impressions',
          styleType: 'light',
        },
        {
          title: 'Organic Clicks',
          value: `${formatNumber(summaryData.organic_clicks)}`,
          change: `${getPercentageChange(diffData.organic_clicks)}`,
          isPositive: getIsPositiveOrNA(diffData.organic_clicks),
          tooltipContent: 'auto_optimize_page.performance.organic_clicks',
          styleType: 'light',
        },
      ];

      setCardData(cardProps);
      setDailyCostVsExpectedData(data.cost_vs_expected_cost);
      setWeeklyCostVsExpectedData(data.cost_vs_expected_cost_weekly);
      setOrganicVsPaidClicksData(data.organic_vs_paid_clicks);
      setOrganicVsPaidImpressionsData(data.organic_vs_paid_impressions);
      setChangeLogData({
        change_log: data.change_log,
        change_log_table: data.change_log_table,
        change_log_daily: data.change_log_daily,
        change_log_summary: data.change_log_summary,
      });
    }
  }, [data]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleValueChange = (newValue: DateValueType) => {
    if (newValue?.startDate && newValue?.endDate) {
      setValue({
        startDate: new Date(newValue.startDate),
        endDate: new Date(newValue.endDate),
      });
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  // HANDLE KEYWORD SELECTIONS
  const onSelectKeywords = (selections: string[]) => {
    setSelectedKeywords(
      selections.length === keywordOptions.length ? '' : selections.join(',')
    );
  };

  const preselectedOptions = useMemo(() => {
    if (keywordOptions && !selectedKeywords) {
      return [...keywordOptions];
    }

    return selectedKeywords.split(',');
  }, [keywordOptions, selectedKeywords]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <SectionMessage variant="error" onRetry={() => refetch()} />;
  }

  return (
    <div className="pt-8">
      <FilterBarWrapper topPos="top-[94px]">
        <div className="flex-1" />
        <TooltipPopover
          content="auto_optimize_page.filter.query"
          type="popup"
          side="top"
        >
          <MultiDropdown
            placeholder={'Query'}
            options={keywordOptions}
            preselectedOptions={preselectedOptions}
            onSelectionChange={(selections) => onSelectKeywords(selections)}
            dropdownLength="max-h-60"
          />
        </TooltipPopover>
        <div className="flex items-center">
          <TooltipPopover
            content="auto_optimize_page.filter.date_range"
            type="popup"
            side="top"
          >
            <DatepickerComponent
              value={dateRange}
              onChange={handleValueChange}
              product="osab"
            />
          </TooltipPopover>
          <TooltipPopover
            content="auto_optimize_page.filter.date_range_with_i"
            type="info"
            className="-top-2 right-1"
            side="top"
          >
            <div className="relative mb-4 mx-2 mr-4 group inline-block"></div>
          </TooltipPopover>
        </div>
      </FilterBarWrapper>
      <div className="bg-transparent border-b border-success mb-4 pb-2 flex justify-between items-center mt-8">
        <div>
          <h2 className="text-primarygray text-2xl font-bold tracking-wide">
            Performance
          </h2>
          <p className="text-gray800 text-sm font-bold leading-[18px]">
            Time frame chose vs. same time period from the prior year
          </p>
        </div>
        <div
          className="relative"
          ref={dropdownRef}
          id="downloadDropdownMenu-autoOptimize"
        >
          <img
            src={`${process.env.PUBLIC_URL}/static/img/dots-vertical.svg`}
            alt="dots"
            className="h-6 w-6 mr-4 cursor-pointer"
            onClick={toggleDropdown}
          />
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-light text-sm text-gray700 rounded-md shadow-lg z-50">
              <ul>
                {/* <li className="px-4 py-2 hover:bg-gray-100 cursor-pointer" onClick={closeDropdown}>View data table</li> */}
                <li
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    closeDropdown();
                    downloadXLS(
                      'autoOptimize',
                      ['card name', 'value'],
                      cardData.map((item) => item.title),
                      [cardData.map((item) => item.value)]
                    );
                  }}
                >
                  Download XLS
                </li>
                <li
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    closeDropdown();
                    downloadImage('autoOptimize');
                  }}
                >
                  Download PNG image
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
      <div
        className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-6 mb-8 whitespace-nowrap"
        id="autoOptimize"
      >
        {cardData.map((props, index) => (
          <Card key={index} {...props} />
        ))}
      </div>
      <CostVsExpectCost
        dailyData={dailyCostVsExpectedData}
        weeklyData={weeklyCostVsExpectedData}
      />
      <OrganicPaidClick data={organicVsPaidClicksData} />
      <OrganicPaidImp data={organicVsPaidImpressionsData} />
      <ChangeLog {...changeLogData} />
    </div>
  );
}

export default AutoOptimizeOverview;
